import { Avatar, cn, User } from "@nextui-org/react";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { LogOut, User2, UserIcon } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-scroll";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Logo } from "../../components/ui/logo";
import { NavBarItem } from "./navbar-item";

export const DesktopNavbar = ({ navItems }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("");
  const [showBackground, setShowBackground] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(window.isLogin || false);
  const { scrollY } = useScroll();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSetActive = (to) => {
    setActiveLink(to);
  };
  const handleSetInactive = () => {
    setActiveLink("");
  };

  useMotionValueEvent(scrollY, "change", (value) => {
    if (value > 100) {
      setShowBackground(true);
    } else {
      setShowBackground(false);
    }
  });

  const handleLinkClick = (item) => {
    if (window.isLocalhost)
      navigate("/wordpress/new-home/", {
        state: { scrollTo: item.link, replace: true },
      });
    else
      navigate("/", {
        state: { scrollTo: item.link, replace: true },
      });
  };

  const logOut = async () => {
    try {
      const response = await fetch(
        window.siteUrl + "/wp-json/whizmanage/v1/log_out",
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (response.ok) {
        window.location.reload();
      } else {
        console.error("Logout failed:", response.statusText);
      }
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  return (
    <motion.div
      className={cn(
        "relative w-full flex justify-between px-4 py-3 rounded-md transition duration-200 mx-auto",
        showBackground
          ? "shadow-md border border-neutral-800/70 bg-[rgba(23,23,23,0.6)]"
          : "bg-transparent"
      )}
      initial={{ background: "rgba(23, 23, 23, 0)" }}
      animate={{
        width: showBackground ? "90%" : "100%",
        background: showBackground
          ? "rgba(23, 23, 23, 0.6)"
          : "rgba(23, 23, 23, 0)",
        backdropFilter: showBackground ? "blur(18px)" : "none",
      }}
      transition={{
        duration: 0.4,
      }}
    >
      <div className="flex flex-row gap-2 items-center">
        <div className="pb-1.5">
          <Logo />
        </div>
        <div className="flex items-center gap-1.5">
          {navItems.map((item) => (
            <div key={item.title}>
              {(window.isLocalhost &&
                location.pathname === "/wordpress/new-home/") ||
              location.pathname === "/new-home/" || location.pathname === "/" ? (
                <Link
                  activeClass="active"
                  spy={true}
                  to={item.link}
                  smooth="easeInOutQuint"
                  duration={1000}
                  delay={200}
                  onSetActive={handleSetActive}
                  onSetInactive={handleSetInactive}
                  offset={-20}
                >
                  <NavBarItem active={activeLink === item.link}>
                    {item.title}
                  </NavBarItem>
                </Link>
              ) : (
                <div onClick={() => handleLinkClick(item)}>
                  <NavBarItem active={activeLink === item.link}>
                    {item.title}
                  </NavBarItem>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="flex space-x-2 items-center">
        {(window.isLocalhost && location.pathname != "/wordpress/new-login/") ||
        location.pathname != "/new-login/" ? (
          !isLoggedIn ? (
            <a
              href={window.siteUrl + "/new-login"}
              className="bg-transparent no-underline group cursor-pointer relative shadow-2xl shadow-zinc-900 rounded-full p-px text-xs font-semibold leading-6 !text-white hover:!text-white inline-block"
            >
              <span className="absolute inset-0 overflow-hidden rounded-full">
                <span className="absolute inset-0 rounded-full bg-[image:radial-gradient(75%_100%_at_50%_0%,rgba(162,28,175,0.6)_0%,rgba(162,28,175,0)_75%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </span>
              <div className="relative flex space-x-2 items-center z-10 rounded-full bg-neutral-950/60 py-0.5 px-4 ring-1 ring-white/10">
                <span>Login</span>
                <svg
                  fill="none"
                  height="16"
                  viewBox="0 0 24 24"
                  width="16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.75 8.75L14.25 12L10.75 15.25"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>
              <span className="absolute -bottom-0 left-[1.125rem] h-px w-[calc(100%-2.25rem)] bg-gradient-to-r from-fuchsia-400/0 via-fuchsia-400/90 to-fuchsia-400/0 transition-opacity duration-500 group-hover:opacity-40" />
            </a>
          ) : (
            <div className="flex h-full items-center gap-4">
              <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger asChild>
                  <div className="!flex items-center !gap-4">
                    <p className="text-lg text-neutral-300">
                      Hi, {window.profileName}
                    </p>
                    <Avatar
                      isPressable
                      isFocusable
                      showFallback
                      name={window.profileName}
                      fallback={
                        <UserIcon
                          className="w-6 h-6 text-white"
                          fill="currentColor"
                        />
                      }
                      isBordered
                      radius="lg"
                      src={window.profileImg}
                    />
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="mt-3 !w-60">
                  <DropdownMenuLabel>
                    <User
                      name={window.profileName}
                      avatarProps={{
                        name: window.profileName,
                        showFallback: true,
                        src: window.profileImg,
                      }}
                    />
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() => {
                        window.location.href = window.siteUrl + "/my-account";
                      }}
                    >
                      <User2 className="mr-2 h-4 w-4" />
                      <span>My account</span>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        ) : (
          <></>
        )}
      </div>
    </motion.div>
  );
};
